import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { QRCode } from 'react-qrcode-logo';
import axios from 'axios';
import config from '../config';

function generateBatchId(product, manufacturer) {
  const prefix = manufacturer
    ? manufacturer.substring(0, 3).toUpperCase().replace(/\s/g, '')
    : 'VRC';
  const date = new Date();
  const dateStr = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  const random = Math.floor(Math.random() * 9000 + 1000);
  return `${prefix}-${dateStr}-${random}`;
}

function RegisterBatch() {
  const [form, setForm] = useState({
    batchId: '',
    manufacturer: '',
    product: '',
    quantity: '',
    expiryDate: '',
    productionDate: '',
    productionSite: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registeredBatch, setRegisteredBatch] = useState(null);

  const username = localStorage.getItem('verichain_username') || '';
  const name = localStorage.getItem('verichain_user') || '';

  // Auto-generate batch ID when manufacturer or product changes
  useEffect(() => {
    if (form.manufacturer || form.product) {
      setForm(prev => ({
        ...prev,
        batchId: generateBatchId(prev.product, prev.manufacturer),
      }));
    }
  }, [form.manufacturer, form.product]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleRegenerateBatchId = () => {
    setForm(prev => ({
      ...prev,
      batchId: generateBatchId(prev.product, prev.manufacturer),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setRegisteredBatch(null);

    try {
      await axios.post(`${config.API_URL}/batch/register`, {
        ...form,
        registeredBy: `${name} (${username})`,
      });
      setRegisteredBatch({
        ...form,
        registeredBy: `${name} (${username})`,
        qrUrl: `${config.API_URL}/verify/${form.batchId}`,
        registeredAt: new Date().toLocaleString('en-ZA'),
      });
      setForm({
        batchId: '',
        manufacturer: '',
        product: '',
        quantity: '',
        expiryDate: '',
        productionDate: '',
        productionSite: '',
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to register batch.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `${registeredBatch.batchId}-qr.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  return (
    <div className="page">
      <Navbar />

      <div style={{ padding: '32px', maxWidth: '900px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#FFFFFF' }}>
            Register Batch
          </h1>
          <p style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>
            Register a product batch on the Verichain blockchain.
          </p>
        </div>

        {/* Logged in as */}
        <div style={{
          backgroundColor: '#0B1D35',
          border: '1px solid #1E3A6E',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f9bc3d, #c28c00)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#0B1D35',
            flexShrink: 0,
          }}>
            {username.charAt(0).toUpperCase()}
          </div>
          <div>
            <p style={{ fontSize: '13px', color: '#FFFFFF', fontWeight: 'bold' }}>{name}</p>
            <p style={{ fontSize: '11px', color: '#64748B' }}>
              {username} · Operator · All actions logged
            </p>
          </div>
        </div>

        {/* Success State */}
        {registeredBatch ? (
          <div style={{ display: 'flex', gap: '24px' }}>

            {/* Batch Details */}
            <div className="card" style={{ flex: 1 }}>
              <div style={{
                backgroundColor: '#0D2A1A',
                border: '1px solid #22A860',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
              }}>
                <p style={{ color: '#22A860', fontSize: '14px', fontWeight: 'bold' }}>
                  Batch Registered on Blockchain
                </p>
                <p style={{ color: '#CBD5E1', fontSize: '12px', marginTop: '4px' }}>
                  Permanently recorded on Polygon by {name} ({username}).
                </p>
              </div>

              <h3 style={{ fontSize: '14px', color: '#F0B429', marginBottom: '16px' }}>
                Batch Details
              </h3>

              {[
                ['Batch ID', registeredBatch.batchId],
                ['Product', registeredBatch.product],
                ['Manufacturer', registeredBatch.manufacturer],
                ['Quantity', `${registeredBatch.quantity} units`],
                ['Production Site', registeredBatch.productionSite || 'N/A'],
                ['Production Date', registeredBatch.productionDate || 'N/A'],
                ['Expiry Date', registeredBatch.expiryDate || 'N/A'],
                ['Registered By', registeredBatch.registeredBy],
                ['Registered At', registeredBatch.registeredAt],
              ].map(([label, value]) => (
                <div key={label} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px 0',
                  borderBottom: '1px solid #1E3A6E',
                }}>
                  <span style={{ fontSize: '12px', color: '#64748B' }}>{label}</span>
                  <span style={{
                    fontSize: '12px',
                    color: label === 'Registered By' ? '#F0B429' : '#FFFFFF',
                  }}>
                    {value}
                  </span>
                </div>
              ))}

              <button
                className="btn-secondary"
                style={{ marginTop: '24px' }}
                onClick={() => setRegisteredBatch(null)}
              >
                Register Another Batch
              </button>
            </div>

            {/* QR Code */}
            <div className="card" style={{ textAlign: 'center', minWidth: '260px' }}>
              <h3 style={{ fontSize: '14px', color: '#F0B429', marginBottom: '8px' }}>
                QR Code
              </h3>
              <p style={{ fontSize: '11px', color: '#64748B', marginBottom: '20px' }}>
                Print and attach to product packaging
              </p>

              <div style={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '8px',
                display: 'inline-block',
                marginBottom: '16px',
              }}>
                <QRCode
                  value={registeredBatch.qrUrl}
                  size={180}
                  bgColor="#FFFFFF"
                  fgColor="#0B1D35"
                  qrStyle="dots"
                />
              </div>

              <p style={{
                fontSize: '10px',
                color: '#64748B',
                marginBottom: '20px',
                wordBreak: 'break-all',
                padding: '0 8px',
              }}>
                {registeredBatch.batchId}
              </p>

              <button
                className="btn-primary"
                onClick={handleDownloadQR}
                style={{ width: '100%', marginBottom: '12px' }}
              >
                Download QR Code
              </button>

              <button
                className="btn-secondary"
                onClick={() => window.print()}
                style={{ width: '100%' }}
              >
                Print QR Code
              </button>
            </div>

          </div>
        ) : (

          /* Registration Form */
          <div className="card">
            <form onSubmit={handleSubmit}>

              {/* Locked registered by */}
              <label className="label">Registered By</label>
              <div style={{
                backgroundColor: '#0B1D35',
                border: '1px solid #1E3A6E',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: '13px', color: '#FFFFFF' }}>
                  {name} ({username})
                </span>
                <span style={{
                  fontSize: '10px',
                  color: '#64748B',
                  backgroundColor: '#1E3A6E',
                  padding: '2px 8px',
                  borderRadius: '4px',
                }}>
                  LOCKED
                </span>
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label className="label">Manufacturer Name</label>
                  <input
                    className="input"
                    name="manufacturer"
                    placeholder="Your company name"
                    value={form.manufacturer}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">Product Name</label>
                  <input
                    className="input"
                    name="product"
                    placeholder="e.g. Sunlight Dishwashing Liquid 750ml"
                    value={form.product}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Auto-generated Batch ID */}
              <label className="label">Batch ID (Auto-generated)</label>
              <div style={{
                backgroundColor: '#0B1D35',
                border: '1px solid #1E3A6E',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: '13px', color: '#F0B429', fontFamily: 'monospace' }}>
                  {form.batchId || 'Enter manufacturer and product first'}
                </span>
                <button
                  type="button"
                  onClick={handleRegenerateBatchId}
                  style={{
                    background: 'none',
                    border: '1px solid #1E3A6E',
                    color: '#64748B',
                    fontSize: '11px',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Regenerate
                </button>
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label className="label">Quantity</label>
                  <input
                    className="input"
                    name="quantity"
                    type="number"
                    placeholder="Number of units"
                    value={form.quantity}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">Production Site</label>
                  <input
                    className="input"
                    name="productionSite"
                    placeholder="e.g. Johannesburg Plant"
                    value={form.productionSite}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label className="label">Production Date</label>
                  <input
                    className="input"
                    name="productionDate"
                    type="date"
                    value={form.productionDate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="label">Expiry Date</label>
                  <input
                    className="input"
                    name="expiryDate"
                    type="date"
                    value={form.expiryDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {error && <p className="error">{error}</p>}

              <button
                className="btn-primary"
                type="submit"
                disabled={loading || !form.batchId}
              >
                {loading ? 'Recording on Blockchain...' : 'Register Batch and Generate QR Code'}
              </button>

            </form>
          </div>
        )}

      </div>
    </div>
  );
}

export default RegisterBatch;